import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";

import {
  BsFillPauseFill,
  BsFillPlayFill,
  BsVolumeMuteFill,
  BsFillVolumeOffFill,
  BsVolumeDownFill,
  BsVolumeUpFill,
} from "react-icons/bs";

import { MdPhotoCamera, MdVideocam } from "react-icons/md";
import { RiVolumeUpFill } from "react-icons/ri";
import { FiUsers, FiUserPlus, FiGrid, FiEdit3 } from "react-icons/fi";

import {
  userProfileInfo,
  check_followship,
  addFollower,
  deleteFollower,
} from "../../api";

import { useUserContext } from "../../context/AuthContext";

import Posts from "../../components/Posts/Posts";
import ProfileCover from "../../components/ProfileCover";
import ToastMessage from "../../components/ToastMessage";
import ProfileFollowship from "../../components/ProfileFollowship";
import ProfilePicture from "../../components/ProfilePicture";

import { getMediaUrl } from "../../lib/utils";

/* =========================================================
   VERTICAL VOLUME
========================================================= */

function VerticalVolume({ value, onChange }) {
  const trackRef = useRef(null);
  const draggingRef = useRef(false);

  const setFromClientY = useCallback((clientY) => {
    const track = trackRef.current;
    if (!track) return;

    const rect = track.getBoundingClientRect();

    let y = clientY - rect.top;
    let ratio = y / rect.height;

    if (ratio < 0) ratio = 0;
    if (ratio > 1) ratio = 1;

    const val = 1 - ratio;

    onChange(Number(val.toFixed(3)));
  }, [onChange]);

  useEffect(() => {
    function onPointerMove(e) {
      if (!draggingRef.current) return;

      const clientY = e.clientY ?? (e.touches && e.touches[0] && e.touches[0].clientY);

      if (clientY != null) setFromClientY(clientY);
    }

    function onPointerUp() {
      draggingRef.current = false;
    }

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("touchmove", onPointerMove, { passive: false });
    window.addEventListener("touchend", onPointerUp);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("touchmove", onPointerMove);
      window.removeEventListener("touchend", onPointerUp);
    };
  }, [setFromClientY]);

  const onPointerDown = (e) => {
    e.preventDefault();

    draggingRef.current = true;

    const clientY = e.clientY ?? (e.touches && e.touches[0] && e.touches[0].clientY);

    if (clientY != null) setFromClientY(clientY);
  };

  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));

  return (
    <div ref={trackRef} onPointerDown={onPointerDown} onTouchStart={onPointerDown} className="flex h-24 w-6 select-none items-center justify-center" aria-hidden>
      <div className="relative h-full w-1.5 overflow-visible rounded-full bg-white/20">
        <div className="absolute bottom-0 left-0 right-0 rounded-full bg-purple-500" style={{ height: `${pct}%` }} />

        <div className="absolute left-1/2 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-purple-500 bg-white shadow" style={{ bottom: `calc(${pct}% - 6px)` }} />
      </div>
    </div>
  );
}

/* =========================================================
   PROFILE
========================================================= */

const Profile = () => {
  const { authUser } = useUserContext();
  const { id } = useParams();

  const [profileOpen, setProfileOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showFollowship, setShowFollowship] = useState(false);
  const [type, setType] = useState();
  const [userInfo, setUserInfo] = useState();
  const [follow, setFollow] = useState();

  const videoRef = useRef(null);
  const volumeRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showVolume, setShowVolume] = useState(false);
  const [mute, setMute] = useState(false);

  /* =========================================================
     VIDEO
  ========================================================= */

  const onLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const onTimeUpdate = () => {
    if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
  };

  const togglePlayPause = () => {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleSeek = (time) => {
    if (!videoRef.current) return;

    videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = useCallback((val) => {
    const v = Math.max(0, Math.min(1, val));

    if (videoRef.current) {
      videoRef.current.volume = v;
      videoRef.current.muted = false;
    }

    setMute(false);
    setVolume(v);
  }, []);

  /* =========================================================
     PROFILE DATA
  ========================================================= */

  useEffect(() => {
    const getUserInfo = async () => {
      try {
        const res = await userProfileInfo(id);
        setUserInfo(res.data);
      } catch (error) {
        console.error("Profile fetch error:", error);
      }
    };

    getUserInfo();
  }, [id]);

  /* =========================================================
     FOLLOW STATUS
  ========================================================= */

  useEffect(() => {
    if (!userInfo?.id) return;

    const checkFollowship = async () => {
      try {
        const res = await check_followship(userInfo.id);
        setFollow(res.data);
      } catch (error) {
        console.error("Followship error:", error);
      }
    };

    checkFollowship();
  }, [userInfo?.id]);

  /* =========================================================
     REFRESH PROFILE
  ========================================================= */

  const refreshProfile = async () => {
    try {
      const res = await userProfileInfo(id);
      setUserInfo(res.data);
    } catch (error) {
      console.error("Profile refresh error:", error);
    }
  };

  /* =========================================================
     FOLLOW
  ========================================================= */

  const add_follower = async (userId) => {
    try {
      const res = await addFollower(userId);

      ToastMessage(res.data.message);

      const followRes = await check_followship(userId);

      setFollow(followRes.data);

      await refreshProfile();
    } catch (error) {
      console.error("Follow error:", error);
    }
  };

  /* =========================================================
     UNFOLLOW
  ========================================================= */

  const unFollow = async (userId) => {
    try {
      const res = await deleteFollower(userId);

      ToastMessage(res.data.message);

      const followRes = await check_followship(userId);

      setFollow(followRes.data);

      await refreshProfile();
    } catch (error) {
      console.error("Unfollow error:", error);
    }
  };

  /* =========================================================
     CLOSE VOLUME
  ========================================================= */

  useEffect(() => {
    function handleClickOutside(event) {
      if (volumeRef.current && !volumeRef.current.contains(event.target)) {
        setShowVolume(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* =========================================================
     MUTE
  ========================================================= */

  const toggleMute = () => {
    if (!videoRef.current) {
      setMute((prev) => !prev);
      return;
    }

    if (mute) {
      videoRef.current.muted = false;

      const restoredVolume = volume === 0 ? 0.5 : volume;

      videoRef.current.volume = restoredVolume;

      setVolume(restoredVolume);
      setMute(false);
    } else {
      videoRef.current.muted = true;
      setMute(true);
    }
  };

  /* =========================================================
     VOLUME ICON
  ========================================================= */

  const VolumeBtns = () => {
    if (mute) return <BsVolumeMuteFill size={22} />;
    if (volume === 0) return <BsFillVolumeOffFill size={22} />;
    if (volume <= 0.3) return <BsVolumeDownFill size={22} />;
    if (volume <= 0.7) return <RiVolumeUpFill size={22} />;

    return <BsVolumeUpFill size={22} />;
  };

  /* =========================================================
     SELECT COVER
  ========================================================= */

  const selectCover = (coverType) => {
    setType(coverType);
    setIsOpen(true);
  };

  return (
    <>
      <div className="min-h-screen w-full bg-gray-50 pb-16">
        <div className="mx-auto w-full max-w-6xl px-3 py-5 sm:px-5 lg:px-6">

          {/* =====================================================
              PROFILE CARD
          ===================================================== */}

          <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">

            {/* =================================================
                COVER
            ================================================= */}

            <div className="relative h-[260px] w-full overflow-hidden bg-black sm:h-[330px] lg:h-[400px]">

              {/* DEFAULT COVER */}

              {!userInfo?.profile_cover && (
                <img src={getMediaUrl(userInfo?.profile_img)} alt="cover" className="h-full w-full object-cover object-center" />
              )}

              {/* =================================================
                  PHOTO COVER - ONE IMAGE ONLY
              ================================================= */}

              {userInfo?.profile_cover && userInfo?.profile_cover_type === "photo" && (
                <img src={getMediaUrl(userInfo?.profile_cover)} alt="Profile cover" className="absolute inset-0 h-full w-full object-cover object-center" />
              )}

              {/* =================================================
                  VIDEO COVER
              ================================================= */}

              {userInfo?.profile_cover && userInfo?.profile_cover_type === "video" && (
                <div className="absolute inset-0 h-full w-full bg-black">

                  <video
                    ref={videoRef}
                    src={getMediaUrl(userInfo?.profile_cover)}
                    onLoadedMetadata={onLoadedMetadata}
                    onTimeUpdate={onTimeUpdate}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => {
                      setIsPlaying(false);
                      setCurrentTime(0);
                    }}
                    className="absolute inset-0 h-full w-full object-cover"
                    playsInline
                    preload="metadata"
                  />

                  {/* SOFT OVERLAY */}

                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/10" />

                  {/* PROGRESS BAR */}

                  <input
                    type="range"
                    min={0}
                    max={duration || 0}
                    step="0.01"
                    value={currentTime}
                    onChange={(e) => handleSeek(parseFloat(e.target.value))}
                    className="absolute left-0 right-0 top-0 z-30 h-1.5 w-full cursor-pointer appearance-none
                    [&::-webkit-slider-runnable-track]:rounded-full
                    [&::-webkit-slider-thumb]:h-3
                    [&::-webkit-slider-thumb]:w-3
                    [&::-webkit-slider-thumb]:appearance-none
                    [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:bg-white
                    [&::-webkit-slider-thumb]:shadow-md
                    [&::-moz-range-thumb]:h-3
                    [&::-moz-range-thumb]:w-3
                    [&::-moz-range-thumb]:rounded-full
                    [&::-moz-range-thumb]:border-0
                    [&::-moz-range-thumb]:bg-white"
                    style={{
                      background: `linear-gradient(to right, #7c3aed ${
                        duration ? (currentTime / duration) * 100 : 0
                      }%, rgba(255,255,255,0.30) ${
                        duration ? (currentTime / duration) * 100 : 0
                      }%)`,
                    }}
                  />

                  {/* PLAY / PAUSE */}

                  <button
                    type="button"
                    onClick={togglePlayPause}
                    aria-label={isPlaying ? "Pause video" : "Play video"}
                    className="absolute left-5 top-5 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/35 text-white shadow-lg backdrop-blur-md transition-all duration-200 hover:scale-105 hover:bg-purple-600 active:scale-95"
                  >
                    {isPlaying ? (
                      <BsFillPauseFill size={24} />
                    ) : (
                      <BsFillPlayFill size={24} className="translate-x-[1px]" />
                    )}
                  </button>

                  {/* VOLUME */}

                  <div ref={volumeRef} className="absolute right-5 top-5 z-30 flex flex-col items-center">
                    <button
                      type="button"
                      onClick={() => setShowVolume((prev) => !prev)}
                      className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/35 text-white shadow-lg backdrop-blur-md transition-all hover:bg-purple-600 active:scale-95"
                      aria-label="Volume"
                    >
                      <VolumeBtns />
                    </button>

                    {showVolume && (
                      <div className="mt-3 rounded-2xl border border-white/10 bg-black/45 px-2 py-3 shadow-xl backdrop-blur-md">
                        <VerticalVolume value={volume} onChange={handleVolumeChange} />

                        <button type="button" onClick={toggleMute} className="mt-2 flex w-full items-center justify-center rounded-lg py-1.5 text-white/80 transition hover:bg-white/10 hover:text-white">
                          <VolumeBtns />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* =================================================
                  EDIT COVER MENU
              ================================================= */}

              {String(id) === String(authUser?.id) && (
                <div className="absolute bottom-4 right-4 z-10 sm:bottom-5 sm:right-5">
                  <Menu as="div" className="relative">

                    {/* ONE BUTTON */}

                    <MenuButton type="button" className="flex h-10 items-center gap-2 rounded-full border border-white/30 bg-black/45 px-4 text-xs font-semibold text-white shadow-lg backdrop-blur-md transition-all hover:bg-purple-600 active:scale-[0.97]">
                      <FiEdit3 size={17} />

                      <span className="hidden sm:inline">Edit cover</span>
                    </MenuButton>

                    {/* MENU */}

                    <MenuItems
                      anchor="top end"
                      transition
                      className="z-[100] mb-2 w-52 origin-bottom-right rounded-2xl border border-gray-100 bg-white p-2 shadow-2xl outline-none transition duration-150 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
                    >

                      {/* PHOTO */}

                      <MenuItem>
                        <button type="button" onClick={() => selectCover("photo")} className="group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-purple-50 data-[focus]:bg-purple-50">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600 transition group-hover:bg-purple-100">
                            <MdPhotoCamera size={20} />
                          </span>

                          <span>
                            <span className="block text-sm font-semibold text-gray-800">Photo</span>

                            <span className="mt-0.5 block text-xs text-gray-400">Upload one cover photo</span>
                          </span>
                        </button>
                      </MenuItem>

                      {/* VIDEO */}

                      <MenuItem>
                        <button type="button" onClick={() => selectCover("video")} className="group mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-purple-50 data-[focus]:bg-purple-50">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600 transition group-hover:bg-purple-100">
                            <MdVideocam size={21} />
                          </span>

                          <span>
                            <span className="block text-sm font-semibold text-gray-800">Video</span>

                            <span className="mt-0.5 block text-xs text-gray-400">Maximum 15 seconds</span>
                          </span>
                        </button>
                      </MenuItem>
                    </MenuItems>
                  </Menu>
                </div>
              )}
            </div>

            {/* =====================================================
                PROFILE INFORMATION
            ===================================================== */}

            <div className="relative px-4 pb-5 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between">
                <div className="flex flex-col items-center sm:flex-row sm:items-end">

                  {/* PROFILE PICTURE */}

                  <div className="relative">
                    <img
                      src={getMediaUrl(userInfo?.profile_img)}
                      alt={`${userInfo?.firstname || ""} ${userInfo?.lastname || ""}`}
                      className="relative z-[5] -mt-16 h-32 w-32 rounded-full border-[5px] border-white bg-white object-cover shadow-lg sm:-mt-20 sm:h-40 sm:w-40"
                    />

                    {String(id) === String(authUser?.id) && (
                      <button
                        type="button"
                        onClick={() => setProfileOpen(true)}
                        className="absolute -bottom-1 right-5 z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-purple-600 text-white shadow-sm transition hover:bg-purple-700"
                        title="Change profile picture"
                      >
                        <MdPhotoCamera size={20} />
                      </button>
                    )}
                  </div>

                  {/* NAME */}

                  <div className="mt-3 text-center sm:mb-3 sm:ml-5 sm:text-left">
                    <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
                      {userInfo?.firstname} {userInfo?.lastname}
                    </h1>
                  </div>
                </div>

                {/* FOLLOW BUTTON */}

                {String(id) !== String(authUser?.id) && (
                  <div className="mt-5 flex justify-center sm:mb-3 sm:mt-0">
                    {follow?.a_follows_b === 1 ? (
                      <button
                        type="button"
                        onClick={() => unFollow(userInfo?.id)}
                        className="min-w-[120px] rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:border-red-100 hover:bg-red-50 hover:text-red-500"
                      >
                        Unfollow
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => add_follower(userInfo?.id)}
                        className="min-w-[120px] rounded-xl bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md active:scale-[0.98]"
                      >
                        Follow
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* =================================================
                  STATS
              ================================================= */}

              <div className="mt-6 grid grid-cols-3 divide-x divide-gray-100 rounded-2xl border border-gray-100 bg-gray-50/60 py-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowFollowship(true);
                    setType("followers");
                  }}
                  className="flex flex-col items-center justify-center"
                >
                  <span className="text-lg font-bold text-gray-900">{userInfo?.followersCount}</span>

                  <span className="mt-1 text-xs text-gray-400">Followers</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowFollowship(true);
                    setType("following");
                  }}
                  className="flex flex-col items-center justify-center"
                >
                  <span className="text-lg font-bold text-gray-900">{userInfo?.followingCount}</span>

                  <span className="mt-1 text-xs text-gray-400">Following</span>
                </button>

                <div className="flex flex-col items-center justify-center">
                  <span className="text-lg font-bold text-gray-900">{userInfo?.postsCount}</span>

                  <span className="mt-1 text-xs text-gray-400">Posts</span>
                </div>
              </div>
            </div>
          </section>

          {/* =====================================================
              FOLLOWERS / FOLLOWING
          ===================================================== */}

          <section className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">

            {/* FOLLOWERS */}

            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
                    <FiUsers size={19} />
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Followers</h3>

                    <p className="mt-0.5 text-xs text-gray-400">People following this profile</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setShowFollowship(true);
                    setType("followers");
                  }}
                  className="rounded-lg px-3 py-2 text-xs font-semibold text-purple-600 transition hover:bg-purple-50"
                >
                  View all
                </button>
              </div>

              <div className="mt-5 flex min-h-[48px] items-center">
                {userInfo?.followersCount === 0 ? (
                  <p className="text-sm text-gray-400">No followers yet.</p>
                ) : (
                  <div className="flex items-center">
                    {(userInfo?.followers || []).slice(0, 6).map((f, index) => (
                      <div key={`${f?.id}-${index}`} className="relative -mr-3 h-11 w-11 shrink-0 overflow-hidden rounded-full border-[3px] border-white bg-gray-100">
                        <img src={getMediaUrl(f?.profile_img)} alt={`${f.firstname} ${f.lastname}`} className="h-full w-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* FOLLOWING */}

            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <FiUserPlus size={19} />
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Following</h3>

                    <p className="mt-0.5 text-xs text-gray-400">Profiles this user follows</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setShowFollowship(true);
                    setType("following");
                  }}
                  className="rounded-lg px-3 py-2 text-xs font-semibold text-purple-600 transition hover:bg-purple-50"
                >
                  View all
                </button>
              </div>

              <div className="mt-5 flex min-h-[48px] items-center">
                {userInfo?.followingCount === 0 ? (
                  <p className="text-sm text-gray-400">No following yet.</p>
                ) : (
                  <div className="flex items-center">
                    {(userInfo?.following || []).slice(0, 6).map((f, index) => (
                      <div key={`${f?.id}-${index}`} className="relative -mr-3 h-11 w-11 shrink-0 overflow-hidden rounded-full border-[3px] border-white bg-gray-100">
                        <img src={getMediaUrl(f?.profile_img)} alt={`${f.firstname} ${f.lastname}`} className="h-full w-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* =====================================================
              POSTS HEADER
          ===================================================== */}

          <div className="mt-8 flex items-center justify-between border-b border-gray-100 pb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
                <FiGrid size={19} />
              </div>

              <div>
                <h2 className="text-lg font-bold text-gray-900">Posts</h2>

                <p className="text-xs text-gray-400">Latest activity from {userInfo?.firstname}</p>
              </div>
            </div>

            <span className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-500">
              {userInfo?.postsCount} posts
            </span>
          </div>

          {/* =====================================================
              POSTS
          ===================================================== */}

          <div className="home-posts mx-auto mt-5 w-full">
            <Posts posts={userInfo?.posts} />
          </div>
        </div>
      </div>

      {/* =========================================================
          MODALS
      ========================================================= */}

      <ProfileCover
        isOpen={isOpen}
        isClose={() => {
          setIsOpen(false);
          refreshProfile();
        }}
        coverType={type}
      />

      <ProfileFollowship
        isOpen={showFollowship}
        isClose={() => setShowFollowship(false)}
        type={type}
        followers={userInfo?.followers}
        following={userInfo?.following}
        followersCount={userInfo?.followersCount}
        followingCount={userInfo?.followingCount}
      />

      <ProfilePicture
        pictureOpen={profileOpen}
        pictureClose={() => {
          setProfileOpen(false);
          refreshProfile();
        }}
      />
    </>
  );
};

export default Profile;